import React, { useState, useEffect } from "react"
import Icon from "../../../components/AppIcon"
import Select from "../../../components/ui/Select"
import { Badge } from "../../../components/ui/badge"
import { listCourseEnrollments } from "../../../utils/enrollmentHelpers"
import { setAttendance } from "../../../utils/cohortHelpers"

// Mirrors CohortLearnersTab's data source (enrollments for the course, filtered
// down to this cohortId) rather than a separate query — same reasoning as
// getCohortLearners in cohortHelpers.js: cohortId already lives on the
// enrollment doc, no dedicated join collection needed.
const CohortAttendanceTab = ({ orgId, cohortId, courseId, scheduleEvents, attendance, onChange }) => {
  const [learners, setLearners] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedEventId, setSelectedEventId] = useState("")
  const [pendingUserId, setPendingUserId] = useState(null)

  useEffect(() => {
    const load = async () => {
      if (!orgId || !courseId) return
      setLoading(true)
      const { enrollments } = await listCourseEnrollments(orgId, courseId)
      setLearners(enrollments?.filter((e) => e?.cohortId === cohortId) || [])
      setLoading(false)
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, courseId, cohortId])

  const sortedEvents = [...(scheduleEvents || [])].sort((a, b) => new Date(a?.date || 0) - new Date(b?.date || 0))

  useEffect(() => {
    if (!selectedEventId && sortedEvents?.length > 0) setSelectedEventId(sortedEvents[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleEvents])

  const eventOptions = sortedEvents?.map((ev) => ({
    value: ev?.id,
    label: `${ev?.title}${ev?.date ? ` — ${ev.date}` : ""}`,
  }))

  const handleToggle = async (userId, present) => {
    setPendingUserId(userId)
    await setAttendance(orgId, cohortId, selectedEventId, userId, present)
    setPendingUserId(null)
    onChange?.()
  }

  const eventAttendance = attendance?.[selectedEventId] || {}
  const presentCount = learners?.filter((l) => eventAttendance?.[l?.userId] === true)?.length || 0

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading attendance...</div>
  }

  if (sortedEvents?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Icon name="ClipboardCheck" size={24} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Add a schedule event first, then take attendance for it here.</p>
      </div>
    )
  }

  if (learners?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Icon name="Users" size={24} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No learners assigned to this cohort yet. Assign learners from the Learners tab first.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="w-72">
          <Select options={eventOptions} value={selectedEventId} onChange={setSelectedEventId} />
        </div>
        <p className="text-sm text-muted-foreground shrink-0">{presentCount} of {learners?.length || 0} present</p>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground uppercase">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Learner</th>
              <th className="text-right px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {learners?.map((l) => {
              const status = eventAttendance?.[l?.userId]
              const isPresent = status === true
              const isAbsent = status === false
              return (
                <tr key={l?.userId} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{l?.learnerName || "Unnamed learner"}</p>
                    <p className="text-xs text-muted-foreground">{l?.learnerEmail || l?.userId}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {isPresent && <Badge variant="success">Present</Badge>}
                      {isAbsent && <Badge variant="destructive">Absent</Badge>}
                      {status === undefined && <Badge variant="secondary">Not marked</Badge>}
                      <button
                        type="button"
                        disabled={pendingUserId === l?.userId}
                        onClick={() => handleToggle(l?.userId, true)}
                        className={`p-1.5 rounded-md border transition-colors ${
                          isPresent ? "border-success bg-success/10 text-success" : "border-border text-muted-foreground hover:text-success hover:border-success"
                        }`}
                      >
                        <Icon name="Check" size={14} />
                      </button>
                      <button
                        type="button"
                        disabled={pendingUserId === l?.userId}
                        onClick={() => handleToggle(l?.userId, false)}
                        className={`p-1.5 rounded-md border transition-colors ${
                          isAbsent ? "border-destructive bg-destructive/10 text-destructive" : "border-border text-muted-foreground hover:text-destructive hover:border-destructive"
                        }`}
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default CohortAttendanceTab